import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Pin,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  MapPin,
  Key,
  Info,
  Copy,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';

interface PinnedNote {
  id: string;
  text: string;
  createdAt: Date;
  isPinned: boolean;
}

interface PinnedNotesDisplayProps {
  notes: PinnedNote[];
  wellName: string;
  compact?: boolean;
}

export default function PinnedNotesDisplay({
  notes,
  wellName,
  compact = false,
}: PinnedNotesDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const pinnedNotes = notes.filter((n) => n.isPinned);

  if (pinnedNotes.length === 0) return null;

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getIcon = (text: string) => {
    const lowerText = text.toLowerCase();
    if (lowerText.includes('gate') || lowerText.includes('code')) {
      return <Key className="h-4 w-4 text-yellow-700" />;
    }
    if (lowerText.includes('location') || lowerText.includes('direction')) {
      return <MapPin className="h-4 w-4 text-yellow-700" />;
    }
    if (lowerText.includes('warning') || lowerText.includes('caution')) {
      return <AlertCircle className="h-4 w-4 text-yellow-700" />;
    }
    return <Info className="h-4 w-4 text-yellow-700" />;
  };

  if (compact) {
    return (
      <Card className="border-2 border-yellow-400 bg-yellow-50 shadow-lg">
        <CardContent className="p-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center space-x-2">
              <Pin className="h-5 w-5 text-yellow-700" />
              <span className="font-semibold text-yellow-900 text-sm">
                {pinnedNotes.length} Pinned Note{pinnedNotes.length > 1 ? 's' : ''}
              </span>
              <Badge className="bg-yellow-600 text-white text-xs">
                Important
              </Badge>
            </div>
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-yellow-700" />
            ) : (
              <ChevronDown className="h-5 w-5 text-yellow-700" />
            )}
          </button>

          {isExpanded && (
            <div className="mt-3 space-y-2">
              {pinnedNotes.map((note) => (
                <div
                  key={note.id}
                  className="bg-white rounded-lg p-2.5 border border-yellow-400"
                >
                  <div className="flex items-start space-x-2">
                    {getIcon(note.text)}
                    <p className="flex-1 text-sm text-gray-900 whitespace-pre-wrap">
                      {note.text}
                    </p>
                    <button
                      onClick={() => handleCopy(note.text, note.id)}
                      className="p-1 hover:bg-yellow-100 rounded transition-colors"
                    >
                      {copiedId === note.id ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4 text-yellow-700" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full display (non-compact)
  return (
    <div className="space-y-2">
      {pinnedNotes.map((note, index) => (
        <Card
          key={note.id}
          className="border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 to-amber-50 shadow-lg"
        >
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center">
                  <Pin className="h-5 w-5 text-yellow-900" />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-2">
                  <Badge className="bg-yellow-600 text-white text-xs">
                    Pinned Note #{index + 1}
                  </Badge>
                  <span className="text-xs text-yellow-800">{wellName}</span>
                </div>

                <div className="bg-white rounded-lg p-3 border-2 border-yellow-300 mb-2">
                  <div className="flex items-start space-x-2">
                    {getIcon(note.text)}
                    <p className="flex-1 text-sm text-gray-900 font-medium whitespace-pre-wrap">
                      {note.text}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-xs text-yellow-700">
                    Pinned on {new Date(note.createdAt).toLocaleDateString()}
                  </p>
                  <button
                    onClick={() => handleCopy(note.text, note.id)}
                    className="px-3 py-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-900 rounded-lg text-xs font-medium transition-colors flex items-center space-x-1"
                  >
                    {copiedId === note.id ? (
                      <>
                        <Check className="h-3 w-3" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
